import React from "react";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import Swal from "sweetalert2";

const MedicationSheetForm = ({ formData, setFormData, handleChange, isLoading }) => {
  // Add new medication row
  const addMedicationRow = () => {
    setFormData((prev) => ({
      ...prev,
      medications: [
        ...prev.medications,
        {
          id: Date.now(),
          drugName: "",
          dose: "",
          frequency: "",
          sign: "",
          startDate: "",
          timeEntries: [],
        },
      ],
    }));
  };

  // Add new time entry row to a specific medication
  const addTimeEntry = (medicationId) => {
    setFormData((prev) => {
      const updatedMedications = prev.medications.map((med) => {
        if (med.id === medicationId) {
          // Calculate next date based on start date and number of existing entries
          let nextDate = "";
          if (med.startDate) {
            try {
              const startDate = new Date(med.startDate);
              startDate.setDate(startDate.getDate() + med.timeEntries.length);
              // Format as YYYY-MM-DD
              nextDate = startDate.toISOString().split('T')[0];
            } catch (error) {
              console.error("Error calculating next date:", error);
            }
          }

          return {
            ...med,
            timeEntries: [
              ...med.timeEntries,
              {
                id: Date.now(),
                given: "", // "Yes" or "No"
                time_date: nextDate ? `${nextDate}T12:00` : "", // Set default time to 12:00 PM
              },
            ],
          };
        }
        return med;
      });

      return {
        ...prev,
        medications: updatedMedications,
      };
    });
  };

  // Remove medication row
  const removeMedicationRow = (id) => {
    if (formData.medications.length === 1) return;

    Swal.fire({
      title: "Delete this medication?",
      text: "This will remove all its time entries as well.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then((res) => {
      if (res.isConfirmed) {
        setFormData((prev) => ({
          ...prev,
          medications: prev.medications.filter((r) => r.id !== id),
        }));
      }
    });
  };

  // Remove time entry row
  const removeTimeEntry = (medicationId, timeEntryId) => {
    Swal.fire({
      title: "Delete this time entry?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then((res) => {
      if (res.isConfirmed) {
        setFormData((prev) => ({
          ...prev,
          medications: prev.medications.map((med) =>
            med.id === medicationId
              ? {
                ...med,
                timeEntries: med.timeEntries.filter(
                  (time) => time.id !== timeEntryId
                ),
              }
              : med
          ),
        }));
      }
    });
  };

  // Handle medication row field changes
  const handleMedicationChange = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      medications: prev.medications.map((r) =>
        r.id === id ? { ...r, [field]: value } : r
      ),
    }));
  };

  // Handle time entry field changes
  const handleTimeEntryChange = (medicationId, timeEntryId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      medications: prev.medications.map((med) =>
        med.id === medicationId
          ? {
            ...med,
            timeEntries: med.timeEntries.map((time) =>
              time.id === timeEntryId
                ? { ...time, [field]: value }
                : time
            ),
          }
          : med
      ),
    }));
  };

  // Format date for display (MM/DD/YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return "";
    }
  };

  // Format time for display (hh:mm AM/PM)
  const formatTimeForDisplay = (dateTimeString) => {
    if (!dateTimeString) return "";
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return "";
    }
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">

      {/* ==== Patient Information ==== */}
      <div className="card shadow-sm border-0 p-6">
        <div className="col-12 mb-4">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
            Patient Information
          </small>
        </div>

        <div className="row g-3 mb-4">
          {[
            { name: "name", placeholder: "Enter Name" },
            // { name: "bedNo", placeholder: "Enter Bed No" },
            { name: "doa", type: "date", placeholder: "Date of Admission" },
            { name: "mrNo", placeholder: "Enter MR No" },
            { name: "age", placeholder: "Enter Age" },
            { name: "consultant", placeholder: "Consultant's Name" },
            // { name: "ward", placeholder: "Enter Ward" },
            // { name: "diagnosis", placeholder: "Enter Diagnosis" },
          ].map((field, i) => (
            <div className="col-md-3" key={i}>
              <input
                type={field.type || "text"}
                name={field.name}
                className="form-control"
                placeholder={field.placeholder}
                value={formData[field.name] || ""}
                onChange={handleChange}
                disabled={isLoading}
                readOnly={field.name === "mrNo" || field.name === "consultant" || ((field.name === "name" || field.name === "age") && (!!formData.patientId || !!formData.patient_id))}
              />
            </div>
          ))}
        </div>

        {/* ==== Medication Details ==== */}
        <div className="col-12 mb-4">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
            Medication Details
          </small>
        </div>

        {formData.medications.map((medication) => (
          <div
            key={medication.id}
            className="border rounded-4 p-3 mb-3 shadow-sm position-relative bg-light-subtle"
          >
            {/* Medication Row Header */}
            <div className="row g-3 align-items-center mb-3">
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Drug Name"
                  value={medication.drugName}
                  onChange={(e) =>
                    handleMedicationChange(
                      medication.id,
                      "drugName",
                      e.target.value
                    )
                  }
                  disabled={isLoading}
                />
              </div>
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Dose"
                  value={medication.dose || ""}
                  onChange={(e) =>
                    handleMedicationChange(
                      medication.id,
                      "dose",
                      e.target.value
                    )
                  }
                  disabled={isLoading}
                />
              </div>
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Frequency"
                  value={medication.frequency || ""}
                  onChange={(e) =>
                    handleMedicationChange(
                      medication.id,
                      "frequency",
                      e.target.value
                    )
                  }
                  disabled={isLoading}
                />
              </div>
              {/* <div className="col-md-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Sign"
                  value={medication.sign}
                  onChange={(e) =>
                    handleMedicationChange(medication.id, "sign", e.target.value)
                  }
                  disabled={isLoading}
                />
              </div> */}
              <div className="col-md-2">
                <input
                  type="date"
                  className="form-control"
                  value={medication.startDate}
                  onChange={(e) =>
                    handleMedicationChange(
                      medication.id,
                      "startDate",
                      e.target.value
                    )
                  }
                  disabled={isLoading}
                />
              </div>
              <div className="col-md-1 text-end">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-lg"
                  onClick={() => removeMedicationRow(medication.id)}
                  disabled={isLoading}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>

            {/* Time Entries Section */}
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="fw-semibold text-secondary">
                  Time / Date Entries
                </div>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={() => addTimeEntry(medication.id)}
                  disabled={isLoading}
                >
                  <i className="ri-add-line me-1"></i> Add Time Entry
                </button>
              </div>

              {medication.timeEntries.map((timeEntry, index) => (
                <div
                  key={timeEntry.id}
                  className="border rounded-3 p-3 mb-3 bg-white"
                >
                  <div className="row g-3 align-items-center">
                    <div className="col-md-3">
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-medium text-secondary">Given?</span>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name={`given-${medication.id}-${timeEntry.id}`}
                            id={`given-yes-${medication.id}-${timeEntry.id}`}
                            value="Yes"
                            checked={timeEntry.given === "Yes"}
                            onChange={(e) =>
                              handleTimeEntryChange(
                                medication.id,
                                timeEntry.id,
                                "given",
                                e.target.value
                              )
                            }
                            disabled={isLoading}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`given-yes-${medication.id}-${timeEntry.id}`}
                          >
                            Yes
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name={`given-${medication.id}-${timeEntry.id}`}
                            id={`given-no-${medication.id}-${timeEntry.id}`}
                            value="No"
                            checked={timeEntry.given === "No"}
                            onChange={(e) =>
                              handleTimeEntryChange(
                                medication.id,
                                timeEntry.id,
                                "given",
                                e.target.value
                              )
                            }
                            disabled={isLoading}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`given-no-${medication.id}-${timeEntry.id}`}
                          >
                            No
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={timeEntry.time_date || ""}
                        onChange={(e) =>
                          handleTimeEntryChange(
                            medication.id,
                            timeEntry.id,
                            "time_date",
                            e.target.value
                          )
                        }
                        disabled={isLoading}
                      />
                    </div>

                    <div className="col-md-3 text-end">
                      <div className="small text-muted mb-1">
                        {timeEntry.time_date ? (
                          <>
                            {formatDateForDisplay(timeEntry.time_date)} {formatTimeForDisplay(timeEntry.time_date)}
                          </>
                        ) : (
                          "No date/time selected"
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() =>
                          removeTimeEntry(medication.id, timeEntry.id)
                        }
                        disabled={isLoading}
                      >
                        <i className="ri-close-line me-1"></i> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {medication.timeEntries.length === 0 && (
                <div className="text-center py-4 border rounded-3 bg-light">
                  <p className="text-muted mb-0">
                    No time entries added yet. Click "Add Time Entry" to add one.
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}

        <div className="text-end mt-3">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={addMedicationRow}
            disabled={isLoading}
          >
            <i className="ri-add-line"></i> Add Medication
          </button>
        </div>

      </div>
    </div>
  );
};

export default MedicationSheetForm;